import React, { useState } from 'react';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const CheckoutPage = () => {
  const { cartItems, totalPrice, clearCart } = useCart();
  const { user, apiRequest } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    // Customer Info (pre-filled from user)
    customerInfo: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
    // Shipping Address
    shippingAddress: {
      street: user?.address?.street || '',
      city: user?.address?.city || '',
      state: user?.address?.state || '',
      zipCode: user?.address?.zipCode || '',
      country: user?.address?.country || 'Nigeria',
    },
    // Billing Address
    billingAddress: {
      street: user?.address?.street || '',
      city: user?.address?.city || '',
      state: user?.address?.state || '',
      zipCode: user?.address?.zipCode || '',
      country: user?.address?.country || 'Nigeria',
    },
    useSameAddress: true,
    paymentMethod: 'paystack',
    notes: '',
  });

  // Calculate totals
  const subtotal = totalPrice;
  const taxRate = 0.08; // 8% tax
  const tax = subtotal * taxRate;
  const shipping = subtotal > 50 ? 0 : 5.99; // Free shipping over $50
  const total = subtotal + tax + shipping;

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSameAddressChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      useSameAddress: checked,
      ...(checked && {
        billingAddress: { ...prev.shippingAddress }
      })
    }));
  };

  const validateForm = () => {
    const { customerInfo, shippingAddress } = formData;
    
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      setError('Please fill in all customer information fields');
      return false;
    }

    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.zipCode) {
      setError('Please fill in all shipping address fields');
      return false;
    }

    return true;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) return;

  setLoading(true);
  setError(null);

  try {
    // Create order payload
    const orderItems = cartItems.map(item => ({
      product: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      image: item.image?.url || item.image
    }));

    const orderData = {
      items: orderItems,
      customerInfo: formData.customerInfo,
      shippingAddress: formData.shippingAddress,
      billingAddress: formData.useSameAddress ? formData.shippingAddress : formData.billingAddress,
      paymentMethod: formData.paymentMethod,
      notes: formData.notes,
      itemsPrice: subtotal,
      taxPrice: tax,
      shippingPrice: shipping,
      totalPrice: total
    };

    // Debug: Log the order data being sent
    console.log('Order data being sent:', orderData);

    // 1. Create order record with improved error handling
    const orderResponse = await apiRequest('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });

    // Get the response text first to see what we're dealing with
    const responseText = await orderResponse.text();
    console.log('Raw response:', responseText);

    if (!orderResponse.ok) {
      let errorMessage = 'Failed to create order';
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.message || errorData.error || errorMessage;
        console.log('Parsed error:', errorData);
      } catch (parseError) {
        console.log('Could not parse error response:', responseText);
      }
      throw new Error(errorMessage);
    }

    // Parse the successful response
    const orderResult = JSON.parse(responseText);
    const orderId = orderResult.data.order._id;

    // 2. Initialize payment
    const paymentData = {
      amount: total,
      email: formData.customerInfo.email,
      orderId: orderId
    };

    console.log('Payment data being sent:', paymentData);

    const paymentResponse = await apiRequest('/payments/paystack/initiate', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });

    if (!paymentResponse.ok) {
      const paymentError = await paymentResponse.text();
      console.log('Payment error:', paymentError);
      throw new Error('Payment initialization failed');
    }

    const paymentResult = await paymentResponse.json();

    // 3. Redirect to Paystack
    clearCart();
    window.location.href = paymentResult.data.authorization_url;

  } catch (error) {
    console.error('Checkout error:', error);
    toast.error(error.message || 'Checkout process failed');
    setError(error.message);
  } finally {
    setLoading(false);
  }
};


  if (cartItems.length === 0) {
    return (
      <div className="bg-softRose min-h-screen py-8">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-slateText mb-6">Add some items to your cart before checkout</p>
          <Link 
            to="/products" 
            className="inline-block px-6 py-3 bg-pinkAccent text-white rounded-lg hover:bg-pink-500 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-softRose min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Checkout</h1>
          <p className="text-slateText">Complete your purchase</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slateText mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.customerInfo.name}
                      onChange={(e) => handleInputChange('customerInfo', 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slateText mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.customerInfo.email}
                      onChange={(e) => handleInputChange('customerInfo', 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slateText mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.customerInfo.phone}
                      onChange={(e) => handleInputChange('customerInfo', 'phone', e.target.value)}
                      className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Shipping Address</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slateText mb-1">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={formData.shippingAddress.street}
                      onChange={(e) => handleInputChange('shippingAddress', 'street', e.target.value)}
                      className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slateText mb-1">
                        City *
                      </label>
                      <input
                        type="text"
                        value={formData.shippingAddress.city}
                        onChange={(e) => handleInputChange('shippingAddress', 'city', e.target.value)}
                        className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slateText mb-1">
                        State *
                      </label>
                      <input
                        type="text"
                        value={formData.shippingAddress.state}
                        onChange={(e) => handleInputChange('shippingAddress', 'state', e.target.value)}
                        className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slateText mb-1">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        value={formData.shippingAddress.zipCode}
                        onChange={(e) => handleInputChange('shippingAddress', 'zipCode', e.target.value)}
                        className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slateText mb-1">
                        Country *
                      </label>
                      <input
                        type="text"
                        value={formData.shippingAddress.country}
                        onChange={(e) => handleInputChange('shippingAddress', 'country', e.target.value)}
                        className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div>
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    id="sameAddress"
                    checked={formData.useSameAddress}
                    onChange={(e) => handleSameAddressChange(e.target.checked)}
                    className="h-4 w-4 text-pinkAccent focus:ring-pinkAccent border-gray-300 rounded"
                  />
                  <label htmlFor="sameAddress" className="ml-2 text-sm text-slateText">
                    Billing address is the same as shipping address
                  </label>
                </div>

                {!formData.useSameAddress && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Address</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slateText mb-1">
                          Street Address *
                        </label>
                        <input
                          type="text"
                          value={formData.billingAddress.street}
                          onChange={(e) => handleInputChange('billingAddress', 'street', e.target.value)}
                          className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slateText mb-1">
                            City *
                          </label>
                          <input
                            type="text"
                            value={formData.billingAddress.city}
                            onChange={(e) => handleInputChange('billingAddress', 'city', e.target.value)}
                            className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slateText mb-1">
                            State *
                          </label>
                          <input
                            type="text"
                            value={formData.billingAddress.state}
                            onChange={(e) => handleInputChange('billingAddress', 'state', e.target.value)}
                            className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slateText mb-1">
                            ZIP Code *
                          </label>
                          <input
                            type="text"
                            value={formData.billingAddress.zipCode}
                            onChange={(e) => handleInputChange('billingAddress', 'zipCode', e.target.value)}
                            className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slateText mb-1">
                            Country *
                          </label>
                          <input
                            type="text"
                            value={formData.billingAddress.country}
                            onChange={(e) => handleInputChange('billingAddress', 'country', e.target.value)}
                            className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Notes */}
              <div>
                <label className="block text-sm font-medium text-slateText mb-1">
                  Order Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-lightGray rounded-md focus:outline-none focus:ring-2 focus:ring-pinkAccent focus:border-pinkAccent"
                  placeholder="Any special instructions for your order..."
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-pinkAccent text-white font-medium rounded-lg hover:bg-pink-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pinkAccent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Processing...' : `Place Order - $${total.toFixed(2)}`}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-sm p-6 h-fit sticky top-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
            
            {/* Cart Items */}
            <div className="space-y-3 mb-6">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-3">
                  <img
                    src={item.image?.url || item.image || '/placeholder-product.jpg'}
                    alt={item.name}
                    className="w-12 h-12 rounded-md object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-slateText">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>

            {/* Pricing Breakdown */}
            <div className="space-y-3 pt-4 border-t border-lightGray">
              <div className="flex justify-between text-sm">
                <span className="text-slateText">Subtotal</span>
                <span className="text-gray-900">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slateText">Tax (8%)</span>
                <span className="text-gray-900">${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slateText">Shipping</span>
                <span className="text-gray-900">
                  {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between text-lg font-medium pt-3 border-t border-lightGray">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mt-6 pt-4 border-t border-lightGray">
              <p className="text-sm text-slateText mb-2">Payment Method</p>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-pinkAccent rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm text-gray-900">Paystack (Card Payment)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;